import type { RangeEntry } from '../model/selection.js';

/**
 * The declared range from a selected origin (design 1276–1287).
 *
 * Must-not #6, and the whole of it: **range appears only from a selected
 * origin**. This component takes an `origin` and a list already resolved from
 * `map.ranges` for that origin — there is no shape of props it accepts that
 * renders an ambient range, and `model/selection.ts` has no function that
 * returns one.
 *
 * **Adjacency is never drawn as range.** Adjacent region ids are a `DetailRow`
 * in the block above, as a list of names with no number on them. Region-map.md
 * puts it plainly: `adjacentTo` is load-bearing for exactly one rule and drawing
 * it as though it implied cheap movement is the same lie in graph form. A–B may
 * be 3, B–C 3, and A–C 20; there is no arithmetic here, and the shortest-path
 * solver the mockup had was deleted in Q3.
 *
 * The heading names the origin, so the numbers can never be read as absolute.
 */
export function RangeList(props: {
  readonly origin: string;
  readonly entries: readonly RangeEntry[];
  readonly onSelect: (region: string) => void;
}): JSX.Element | null {
  if (props.entries.length === 0) return null;
  return (
    <div
      data-testid="range-list"
      data-origin={props.origin}
      style={{
        flex: 'none',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 14px 8px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <span
        style={{
          paddingBottom: 6,
          fontFamily: 'var(--font-data)',
          fontSize: 11,
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        RANGE FROM {props.origin}
      </span>
      {props.entries.map((entry) => (
        <button
          key={entry.region}
          type="button"
          data-testid="range-row"
          data-region={entry.region}
          onClick={() => props.onSelect(entry.region)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '6px 0',
            background: 'transparent',
            border: 0,
            borderBottom: '1px solid var(--rule-faint)',
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              flex: 'none',
              width: 60,
              fontFamily: 'var(--font-data)',
              fontSize: 11,
              fontWeight: 600,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--text)',
            }}
          >
            {entry.region}
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontFamily: 'var(--font-prose)',
              fontSize: 12,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--text-3)',
            }}
          >
            {entry.name.toUpperCase() === entry.region.toUpperCase() ? '' : entry.name}
          </span>
          <span
            data-testid="range-value"
            style={{
              flex: 'none',
              fontFamily: 'var(--font-data)',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--text-2)',
            }}
          >
            {entry.range}
          </span>
        </button>
      ))}
    </div>
  );
}
